import { useState } from 'react';
import { createEmail } from '../utils/api';

interface CreatePixelModalProps {
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

export function CreatePixelModal({
  onClose,
  onSuccess,
  onError,
}: CreatePixelModalProps) {
  const [toEmail, setToEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    pixelUrl: string;
    imgTag: string;
  } | null>(null);

  const handleCreate = async () => {
    setLoading(true);
    try {
      const data = await createEmail({
        to_email: toEmail || undefined,
        subject: subject || undefined,
        source: 'dashboard',
      });
      setResult(data);
      onSuccess('Tracking pixel created!');
    } catch (err) {
      onError('Failed to create tracking pixel');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      onSuccess(`${label} copied to clipboard!`);
    } catch (err) {
      onError('Failed to copy to clipboard');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Create Tracking Pixel</h3>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: 'var(--gray-400)',
            }}
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          {!result ? (
            <>
              <div className="input-group">
                <label>To Email (optional)</label>
                <input
                  type="email"
                  className="input"
                  value={toEmail}
                  onChange={(e) => setToEmail(e.target.value)}
                  placeholder="recipient@example.com"
                />
              </div>
              <div className="input-group">
                <label>Subject (optional)</label>
                <input
                  type="text"
                  className="input"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Email subject"
                />
              </div>
            </>
          ) : (
            <>
              <div className="input-group">
                <label>Pixel URL</label>
                <div className="code-block">
                  {result.pixelUrl}
                  <button
                    className="copy-btn"
                    onClick={() => copyToClipboard(result.pixelUrl, 'URL')}
                  >
                    Copy
                  </button>
                </div>
              </div>
              <div className="input-group">
                <label>HTML Image Tag</label>
                <div className="code-block">
                  {result.imgTag}
                  <button
                    className="copy-btn"
                    onClick={() => copyToClipboard(result.imgTag, 'HTML')}
                  >
                    Copy
                  </button>
                </div>
              </div>
              <p style={{ color: 'var(--gray-500)', fontSize: '0.875rem' }}>
                Paste the HTML tag at the end of your email body to track opens.
              </p>
            </>
          )}
        </div>

        <div className="modal-footer">
          {!result ? (
            <>
              <button className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCreate}
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Pixel'}
              </button>
            </>
          ) : (
            <button className="btn btn-primary" onClick={onClose}>
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
